/* eslint-disable react-refresh/only-export-components -- router config module:
   the lazy() route consts are not Fast-Refresh component exports, and this file
   intentionally only exports the `router` instance. */
import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';

import { env } from '@/env';
import { chessBotAcceso } from '@/lib/chess/chessBotAcceso';
import { chessLimuloAcceso } from '@/lib/chess/chessLimuloAcceso';
import { qcpAcceso } from '@/lib/qcp/qcpAcceso';
import { AppShell } from '@/components/layout/AppShell';
import { Loader } from '@/components/common/Loader';
import { CancelloBridge } from '@/components/bridge/CancelloBridge';
import { RoleGate } from '@/components/common/RoleGate';
import { PaginaSondaCattura } from '@/lib/sondaCattura';
import { CreatoreGate } from '@/components/avatars/CreatoreGate';
import type { SchedaAvatar } from '@/lib/avatarSchede';
import { LIMULO_PATHS, LIMULO_ROUTE_PREFIX } from '@/lib/limulo/routes';
import { PaginaMinigiochi } from '@/lib/minigiochi';
import { SchermataModalita, V3_IN_BUILD } from '@/lib/v3';
import { VistaTorneoV3 } from '@/lib/v3';
import { AuthCallback } from './AuthCallback';
import { RouteErrorBoundary } from './RouteErrorBoundary';
import { Hub } from './Hub';
import { Tournaments } from './Tournaments';
import { TournamentDetail } from './TournamentDetail';
import { WatchParty } from './WatchParty';
import { MatchDetail } from './MatchDetail';
import { TeamDetail } from './TeamDetail';
import { ChatList } from './ChatList';
import { ChatRoom } from './ChatRoom';
import { Profile } from './Profile';
import { UserProfile } from './UserProfile';
import { DM_PATH_CONVERSAZIONE } from '@/lib/qsocials/dm';
import { Leaderboards } from './Leaderboards';
import { Inbox } from './Inbox';
import { League } from './League';
import { LeaderboardDetail } from './LeaderboardDetail';
import { News } from './News';
import { ArticleDetail } from './ArticleDetail';
import { NewsTag } from './NewsTag';
import { NewsPreferences } from './NewsPreferences';
import { Preferences } from './settings/Preferences';
import { SettingsHome } from './settings/SettingsHome';
import { Appearance } from './settings/Appearance';
import { NicknameSettings } from './settings/NicknameSettings';
import { LinkedAccounts } from './settings/LinkedAccounts';
import { AuthLinkCallback } from './AuthLinkCallback';
import { WalletLevels } from './settings/WalletLevels';
import { BannerSettings } from './settings/BannerSettings';
import { Communication } from './settings/Communication';
import { StudioAvatar } from './settings/StudioAvatar';
import { Friends } from './Friends';
import { FriendsBlocked } from './FriendsBlocked';
import { FriendsNotifiche } from './FriendsNotifiche';
import { BLOCCATI_ROUTE } from '@/lib/qsocials/bloccati';
import { NOTIFICHE_ROUTE } from '@/lib/notifiche/consenso';
import { DuelLink } from './duels/DuelLink';
import { InviteLanding } from './InviteLanding';
import { IncontroLanding } from './IncontroLanding';

// DuelResult is lazy: it pulls the QBot card catalog + saved-deck + social
// surfaces, only reached after a completed duel — keep it out of the main bundle.
// La conversazione DM si carica solo quando ci si entra (e la rotta esiste solo con i DM accesi).
const DmConversazione = lazy(() => import('./DmConversazione').then((m) => ({ default: m.DmConversazione })));
const DuelResult = lazy(() => import('./duels/DuelResult').then((m) => ({ default: m.DuelResult })));
const DuelTournamentDetail = lazy(() =>
  import('./duels/DuelTournamentDetail').then((m) => ({ default: m.DuelTournamentDetail })),
);

// ── Code-split route trees ────────────────────────────────────────────────
// Avatar, Riot and Clash Royale screens are lazy so their component code
// lands in dedicated chunks (see vite.config.ts manualChunks). Combined with
// the game-conditional Hub, a Clash Royale player never navigates toward the
// Riot tree and so never downloads its chunk. The RTK Query slices themselves
// (qbotApi/qriotsApi/avatarApi) stay in the main bundle for now because the
// store imports them eagerly — disaccoppiamento is a follow-up.

// Avatars
const Avatars = lazy(() => import('./Avatars').then((m) => ({ default: m.Avatars })));
const AvatarCreate = lazy(() => import('./AvatarCreate').then((m) => ({ default: m.AvatarCreate })));
const AvatarDetail = lazy(() => import('./AvatarDetail').then((m) => ({ default: m.AvatarDetail })));
const AvatarEdit = lazy(() => import('./AvatarEdit').then((m) => ({ default: m.AvatarEdit })));
const AvatarSeries = lazy(() => import('./AvatarSeries').then((m) => ({ default: m.AvatarSeries })));
const AvatarClipDetail = lazy(() =>
  import('./AvatarClipDetail').then((m) => ({ default: m.AvatarClipDetail })),
);
// I-581 (ADR-0218): «Le mie emote». Solo a flag acceso (staging): v. `avatarRoutes`.
// ⚠️ DUE letture del flag, e servono tutte e due: `env.featureAvatarEmotesEnabled` decide se la rotta ESISTE (i test lo accendono col mock
// di `@/env`); il confronto LETTERALE dentro il `lazy()` e' quello che il bundler piega, cosi' in produzione l'`import()` sparisce e la
// schermata (col suo marcatore `data-testid="avatar-emotes"`) non finisce in nessun file della build.
const AvatarEmotes = lazy(() =>
  import.meta.env.VITE_FEATURE_AVATAR_EMOTES_ENABLED === 'true'
    ? import('./AvatarEmotes').then((m) => ({ default: m.AvatarEmotes }))
    : Promise.reject(new Error('avatar emotes: la schermata non e compilata in questa build')),
);
// I-612: il negozio degli avatar, SOLO il riscatto gratuito. Solo a flag acceso (staging): v. `avatarRoutes`. Stesse DUE letture del flag di
// `AvatarEmotes`: `env.featureAvatarShopEnabled` decide se la rotta ESISTE (i test lo accendono col mock di `@/env`), il confronto
// LETTERALE dentro il `lazy()` e' quello che il bundler piega, cosi' in produzione l'`import()` sparisce e la schermata (col suo
// marcatore `data-testid="avatar-negozio"`) non finisce in nessun file della build.
const AvatarNegozio = lazy(() =>
  import.meta.env.VITE_FEATURE_AVATAR_SHOP_ENABLED === 'true'
    ? import('./AvatarNegozio').then((m) => ({ default: m.AvatarNegozio }))
    : Promise.reject(new Error('avatar shop: la schermata non e compilata in questa build')),
);
// La sezione Avatar a SCHEDE (I-635/I-636): testata + schede (`AvatarSezione`) e la scheda «Editor» (`AvatarStudio`). Solo a flag acceso (staging): v. `avatarRoutes`.
// ⚠️ DUE letture del flag, come `AvatarEmotes`: `env.featureAvatarSchedeEnabled` decide se le rotte nuove ESISTONO e se le esistenti si montano nella sezione
// (i test lo accendono col mock di `@/env`), il confronto LETTERALE dentro il `lazy()` e' quello che il bundler piega: in produzione i due file non entrano in
// nessun chunk (il marcatore della sezione e' a 0 file nella build di produzione, `tools/verify-avatar-schede.ts`).
const AvatarSezione = lazy(() =>
  import.meta.env.VITE_FEATURE_AVATAR_SCHEDE_ENABLED === 'true'
    ? import('./AvatarSezione').then((m) => ({ default: m.AvatarSezione }))
    : Promise.reject(new Error('avatar schede: la sezione non e compilata in questa build')),
);
const AvatarStudio = lazy(() =>
  import.meta.env.VITE_FEATURE_AVATAR_SCHEDE_ENABLED === 'true'
    ? import('./AvatarStudio').then((m) => ({ default: m.AvatarStudio }))
    : Promise.reject(new Error('avatar schede: la scheda Editor non e compilata in questa build')),
);
// I-699: la scheda «Qpets» (prototipo di collaudo, solo staging). Stesso patto di `AvatarEmotes`: `env.featureQpetsEnabled` decide se la rotta ESISTE, il confronto
// letterale dentro il `lazy()` e' quello che il bundler piega, cosi' il codice Qpets non finisce in nessun file della build di produzione (`tools/verify-qpets.ts`).
const QpetsScheda = lazy(() =>
  import.meta.env.VITE_FEATURE_QPETS_ENABLED === 'true'
    ? import('@/components/qpets/QpetsScheda').then((m) => ({ default: m.QpetsScheda }))
    : Promise.reject(new Error('qpets: la scheda non e compilata in questa build')),
);
const AvatarEditor = lazy(() =>
  import('./avatar-editor/AvatarEditor').then((m) => ({ default: m.AvatarEditor })),
);
const AvatarAssistant = lazy(() =>
  import('./avatar-assistant/AvatarAssistant').then((m) => ({ default: m.AvatarAssistant })),
);

// Riot
const Riot = lazy(() => import('./Riot').then((m) => ({ default: m.Riot })));
const RiotLolDetail = lazy(() => import('./RiotLolDetail').then((m) => ({ default: m.RiotLolDetail })));
const RiotLolMastery = lazy(() =>
  import('./RiotLolMastery').then((m) => ({ default: m.RiotLolMastery })),
);
const RiotLolMatches = lazy(() =>
  import('./RiotLolMatches').then((m) => ({ default: m.RiotLolMatches })),
);
const RiotLolStats = lazy(() => import('./RiotLolStats').then((m) => ({ default: m.RiotLolStats })));
const RiotMultiSearch = lazy(() =>
  import('./RiotMultiSearch').then((m) => ({ default: m.RiotMultiSearch })),
);
const RiotCompare = lazy(() =>
  import('./RiotCompare').then((m) => ({ default: m.RiotCompare })),
);
const RiotSaved = lazy(() => import('./RiotSaved').then((m) => ({ default: m.RiotSaved })));
const RiotTftDetail = lazy(() => import('./RiotTftDetail').then((m) => ({ default: m.RiotTftDetail })));

// Clash Royale
const CRDecks = lazy(() => import('./cr/CRDecks').then((m) => ({ default: m.CRDecks })));
const CRLink = lazy(() => import('./cr/CRLink').then((m) => ({ default: m.CRLink })));
const CRSavedDecks = lazy(() => import('./cr/CRSavedDecks').then((m) => ({ default: m.CRSavedDecks })));
const CRPopularDecks = lazy(() =>
  import('./cr/CRPopularDecks').then((m) => ({ default: m.CRPopularDecks })),
);
const CRBattles = lazy(() => import('./cr/CRBattles').then((m) => ({ default: m.CRBattles })));
const CRBattleDetail = lazy(() =>
  import('./cr/CRBattleDetail').then((m) => ({ default: m.CRBattleDetail })),
);
const CRPlayerProfile = lazy(() =>
  import('./cr/CRPlayerProfile').then((m) => ({ default: m.CRPlayerProfile })),
);
const CRDeckCheck = lazy(() => import('./cr/CRDeckCheck').then((m) => ({ default: m.CRDeckCheck })));
const CRDeckBuilder = lazy(() =>
  import('./cr/CRDeckBuilder').then((m) => ({ default: m.CRDeckBuilder })),
);

// QChess — chess tournaments + duels on Lichess + QLASH leaderboard.
const Chess = lazy(() => import('./Chess').then((m) => ({ default: m.Chess })));
const ChessTournaments = lazy(() =>
  import('./ChessTournaments').then((m) => ({ default: m.ChessTournaments })),
);
const ChessTournamentDetail = lazy(() =>
  import('./ChessTournamentDetail').then((m) => ({ default: m.ChessTournamentDetail })),
);
const ChessChallenges = lazy(() =>
  import('./ChessChallenges').then((m) => ({ default: m.ChessChallenges })),
);
const ChessChallengeDetail = lazy(() =>
  import('./ChessChallengeDetail').then((m) => ({ default: m.ChessChallengeDetail })),
);
const ChessLeaderboard = lazy(() =>
  import('./ChessLeaderboard').then((m) => ({ default: m.ChessLeaderboard })),
);
const ChessBrackets = lazy(() =>
  import('./ChessBrackets').then((m) => ({ default: m.ChessBrackets })),
);
const ChessBracketDetail = lazy(() =>
  import('./ChessBracketDetail').then((m) => ({ default: m.ChessBracketDetail })),
);

// QClans — Clash of Clans stats / war tracking / competitions / AI coaching.
const CocHome = lazy(() => import('./coc/CocHome').then((m) => ({ default: m.CocHome })));
const CocLink = lazy(() => import('./coc/CocLink').then((m) => ({ default: m.CocLink })));
const CocProfile = lazy(() => import('./coc/CocProfile').then((m) => ({ default: m.CocProfile })));
const CocClan = lazy(() => import('./coc/CocClan').then((m) => ({ default: m.CocClan })));
const CocCompetitions = lazy(() =>
  import('./coc/CocCompetitions').then((m) => ({ default: m.CocCompetitions })),
);
const CocCompetitionDetail = lazy(() =>
  import('./coc/CocCompetitionDetail').then((m) => ({ default: m.CocCompetitionDetail })),
);
const CocCoach = lazy(() => import('./coc/CocCoach').then((m) => ({ default: m.CocCoach })));

// QGammon — real-time multiplayer backgammon (leaderboard + live play).
const BackgammonHome = lazy(() =>
  import('./backgammon/BackgammonHome').then((m) => ({ default: m.BackgammonHome })),
);
const Missions = lazy(() =>
  import('./missioni/Missions').then((m) => ({ default: m.Missions })),
);
const ChessMatch = lazy(() => import('./chess/ChessMatch').then((m) => ({ default: m.ChessMatch })));
const BackgammonMatch = lazy(() =>
  import('./backgammon/BackgammonMatch').then((m) => ({ default: m.BackgammonMatch })),
);
const BackgammonReplay = lazy(() =>
  import('./backgammon/BackgammonReplay').then((m) => ({ default: m.BackgammonReplay })),
);
const BackgammonWatch = lazy(() =>
  import('./backgammon/BackgammonWatch').then((m) => ({ default: m.BackgammonWatch })),
);

/**
 * Le rotte della creazione e della spesa sugli avatar (I-416): dietro `<CreatoreGate>` (UNO dei ruoli creatore, `env.avatarCreatorRoles`) SOLO col flag di solo
 * staging `featureAvatarStudioEnabled` acceso; a flag spento (produzione) il nodo resta com'era, senza guardia (I-270).
 */
function soloCreatoriConStudio(node: ReactNode): ReactNode {
  return env.featureAvatarStudioEnabled ? <CreatoreGate>{node}</CreatoreGate> : node;
}

/** Wrap a lazy route element in a Suspense boundary with the app Loader. */
function lazyRoute(node: ReactNode): ReactNode {
  return <Suspense fallback={<Loader />}>{node}</Suspense>;
}

// Routes gated by env feature flags. When the corresponding feature is
// disabled (PROD), the route entry is omitted from the children array — a
// direct URL like `/avatars`, `/riot`, or `/cr` falls through to the `*`
// catch-all and redirects to `/`. Filtering at module load is fine: env
// values are frozen at build time, so the router never needs to re-evaluate.
/**
 * Con le SCHEDE accese (solo staging) il contenuto di una rotta avatar sta dentro `AvatarSezione` (testata + schede); spente, il nodo e' quello di oggi.
 * Il Suspense interno tiene visibili testata e schede mentre il contenuto (lazy) si carica.
 */
function inScheda(scheda: SchedaAvatar | 'qpets', node: ReactNode): ReactNode {
  return env.featureAvatarSchedeEnabled ? lazyRoute(<AvatarSezione scheda={scheda}>{lazyRoute(node)}</AvatarSezione>) : lazyRoute(node);
}

const avatarRoutes: RouteObject[] = env.featureAvatarsEnabled
  ? [
      { path: 'avatars', element: env.featureAvatarSchedeEnabled ? inScheda('mine', <Avatars scheda="mine" />) : lazyRoute(<Avatars />) },
      // Le DUE rotte nuove delle schede: SOLO a flag acceso (a flag spento `avatars/esplora` e `avatars/studio` cadrebbero su `avatars/:id`), e PRIMA di `avatars/:id`.
      // «Editor» col cancello di oggi dei creatori (`soloCreatoriConStudio`, #694): `tools/verify-avatar-schede.ts` tiene queste righe dentro il ramo del flag.
      ...(env.featureAvatarSchedeEnabled
        ? [
            { path: 'avatars/esplora', element: inScheda('explore', <Avatars scheda="explore" />) },
            { path: 'avatars/studio', element: soloCreatoriConStudio(inScheda('studio', <AvatarStudio />)) },
          ]
        : []),
      // I-699: la scheda «Qpets», col SUO flag (in AND con le schede), PRIMA di `avatars/:id` come le altre.
      ...(env.featureAvatarSchedeEnabled && env.featureQpetsEnabled ? [{ path: 'avatars/qpets', element: inScheda('qpets', <QpetsScheda />) }] : []),
      // I-416: con il flag Studio acceso (solo staging, I-270) creare e spendere e' di `administrator` (Qavatar#10
      // risponde 403 `avatar_creation_reserved` a tutti gli altri). Le tre rotte che portano alle rotte riservate
      // stanno dietro lo stesso ruolo: un link diretto non deve aprire una schermata che puo' solo fallire.
      // Flag spento: rotte aperte come oggi.
      { path: 'avatars/new', element: soloCreatoriConStudio(lazyRoute(<AvatarCreate />)) },
      { path: 'avatars/new/assistant', element: soloCreatoriConStudio(lazyRoute(<AvatarAssistant />)) },
      // I-581, ADR-0218: la scelta delle emote dell'avatar. SOLO staging (I-270): a flag spento la rotta non esiste e `avatars/emotes`
      // cadrebbe su `avatars/:id` con un id che non c'e'; `tools/verify-avatar-emotes.ts` tiene questa riga dentro il ramo del flag.
      ...(env.featureAvatarEmotesEnabled ? [{ path: 'avatars/emotes', element: inScheda('emotes', <AvatarEmotes />) }] : []),
      // I-612: il negozio (solo il riscatto gratuito). SOLO staging (I-270): a flag spento la rotta non esiste e `avatars/negozio` cadrebbe su
      // `avatars/:id`; `tools/verify-avatar-shop.ts` tiene questa riga dentro il ramo del flag. PRIMA di `avatars/:id`, come `avatars/emotes`.
      ...(env.featureAvatarShopEnabled ? [{ path: 'avatars/negozio', element: inScheda('shop', <AvatarNegozio />) }] : []),
      { path: 'avatars/:id', element: lazyRoute(<AvatarDetail />) },
      { path: 'avatars/:id/edit', element: soloCreatoriConStudio(lazyRoute(<AvatarEdit />)) },
      { path: 'avatars/:id/series', element: lazyRoute(<AvatarSeries />) },
      { path: 'avatars/:id/clips/:clipId', element: lazyRoute(<AvatarClipDetail />) },
    ]
  : [];

const riotRoutes: RouteObject[] = env.featureQriotsEnabled
  ? [
      { path: 'riot', element: lazyRoute(<Riot />) },
      { path: 'riot/saved', element: lazyRoute(<RiotSaved />) },
      { path: 'riot/multi-search', element: lazyRoute(<RiotMultiSearch />) },
      { path: 'riot/compare', element: lazyRoute(<RiotCompare />) },
      { path: 'riot/lol/:puuid', element: lazyRoute(<RiotLolDetail />) },
      { path: 'riot/lol/:puuid/matches', element: lazyRoute(<RiotLolMatches />) },
      { path: 'riot/lol/:puuid/stats', element: lazyRoute(<RiotLolStats />) },
      { path: 'riot/lol/:puuid/mastery', element: lazyRoute(<RiotLolMastery />) },
      { path: 'riot/tft/:puuid', element: lazyRoute(<RiotTftDetail />) },
    ]
  : [];

const crRoutes: RouteObject[] = env.featureClashRoyaleEnabled
  ? [
      // /cr is now a redirect to the Mazzi landing (compat with old links /
      // the CRHubTile). The deck-focused landing is /cr/decks.
      { path: 'cr', element: <Navigate to="/cr/decks" replace /> },
      { path: 'cr/decks', element: lazyRoute(<CRDecks />) },
      { path: 'cr/link', element: lazyRoute(<CRLink />) },
      { path: 'cr/decks/saved', element: lazyRoute(<CRSavedDecks />) },
      { path: 'cr/decks/popular', element: lazyRoute(<CRPopularDecks />) },
      { path: 'cr/decks/check', element: lazyRoute(<CRDeckCheck />) },
      { path: 'cr/decks/builder', element: lazyRoute(<CRDeckBuilder />) },
      { path: 'cr/battles', element: lazyRoute(<CRBattles />) },
      { path: 'cr/battles/:idx', element: lazyRoute(<CRBattleDetail />) },
      { path: 'cr/player/:tag', element: lazyRoute(<CRPlayerProfile />) },
    ]
  : [];

// Gli scacchi su Limulo (E6 di ADR-0193, P4a): il TAVOLO. Sta sotto un percorso che il vecchio `/chess` di Lichess non ha
// (`chess/match/:id`, e `chess/tournaments/:id` non lo copre), quindi i due insiemi coesistono e nessuno dei due flag spegne
// l'altro. Dietro UN predicato (`chessLimuloAcceso()`, lo stesso del selettore dei giochi e della Hub) e `<RoleGate role="beta">`,
// come il backgammon: una rotta raggiungibile senza la scheda che ci porta sarebbe un ingresso che il flag non chiude.
// ⚠️ Il percorso non e' dichiarato in produzione: `.env.production` non porta il flag, quindi la rotta non esiste li'.
const chessLimuloRoutes: RouteObject[] = chessLimuloAcceso()
  ? [
      {
        path: 'chess/match/:id',
        element: <RoleGate role="beta">{lazyRoute(<ChessMatch />)}</RoleGate>,
      },
    ]
  : [];

// La PARTITA CONTRO IL BOT degli scacchi (E7 di ADR-0193; Luca, I-561/I-562): `/chess/bot`, la scelta di Elo, orologio e colore.
// OPT-IN e acceso SOLO in `.env.staging` (`VITE_FEATURE_CHESS_BOT_ENABLED`, `tools/verify-bot-scacchi.ts`), in AND con gli scacchi
// su Limulo, dietro `<RoleGate role="beta">` come il tavolo. Il percorso sta sotto `chess/` ma non copre ne' e' coperto dalle rotte
// del vecchio `/chess` di Lichess (`chess/tournaments|brackets|challenges|leaderboard`). DUE letture del flag, e servono tutte e due:
// `chessBotAcceso()` decide se la rotta ESISTE (i test accendono `env.featureChessBotEnabled` col mock di `@/env`); il confronto
// LETTERALE dentro il `lazy()` e' quello che il bundler piega, cosi' in produzione l'`import()` sparisce e la schermata (col suo
// marcatore `data-bot-scacchi`) non finisce in nessun file della build.
function chessBotRouteObjects(): RouteObject[] {
  const ChessBot = lazy(() =>
    import.meta.env.VITE_FEATURE_CHESS_BOT_ENABLED === 'true'
      ? import('./chess/ChessBot').then((m) => ({ default: m.ChessBot }))
      : Promise.reject(new Error('chess bot: la schermata non e compilata in questa build')),
  );
  return [
    {
      path: 'chess/bot',
      element: <RoleGate role="beta">{lazyRoute(<ChessBot />)}</RoleGate>,
    },
  ];
}
const chessBotRoutes: RouteObject[] = chessBotAcceso() ? chessBotRouteObjects() : [];

// QBridge: la sezione `/bridge`. OPT-IN e accesa SOLO in `.env.staging` (`VITE_FEATURE_BRIDGE_ENABLED`, `tools/verify-bridge.ts`).
// NESSUNA voce di menu, Hub o selettore giochi: si apre da URL.
//  · R0 (consegna 8): `/bridge/smazzata`, uno scheletro che mostra una smazzata dai robot, dietro `<RoleGate role="beta">`.
//  · R1 K9a «onboarding da ospite» (condizione 4 di tma-orcr): `/bridge` (l'ingresso), `/bridge/prima-mano`, `/bridge/lezioni` e
//    `/bridge/lezioni/:id`, dietro `<CancelloBridge>` = «beta OPPURE ospite».
//  · R1 K9a-2 «pratica col robot base» (condizione 1 di tma-orcr per K9a-2): le rotte che GIOCANO senza rete, `/bridge/lezioni/:id/pratica/:mano`,
//    `/bridge/minibridge` e `/bridge/minibridge/:mano`, anch'esse dietro `<CancelloBridge>`. Resta dietro `RoleGate beta` tutto cio' che apre una
//    rete o e' la pratica LIBERA (E1) e il tavolo.
// DUE letture del flag, e servono tutte e due: `env.featureBridgeEnabled` decide se le rotte ESISTONO (e i test lo accendono con il mock
// di `@/env`); il confronto LETTERALE dentro ogni `lazy()` e' quello che il bundler piega, cosi' in produzione gli `import()` spariscono e
// le pagine — con le fixture, le lezioni copiate e le regole — non finiscono nella build.
function bridgeRouteObjects(): RouteObject[] {
  const BridgeDemo = lazy(() =>
    import.meta.env.VITE_FEATURE_BRIDGE_ENABLED === 'true'
      ? import('./bridge/BridgeDemo').then((m) => ({ default: m.BridgeDemo }))
      : Promise.reject(new Error('bridge: la sezione non e compilata in questa build')),
  );
  const BridgeIngresso = lazy(() =>
    import.meta.env.VITE_FEATURE_BRIDGE_ENABLED === 'true'
      ? import('./bridge/BridgeIngresso').then((m) => ({ default: m.BridgeIngresso }))
      : Promise.reject(new Error('bridge: la sezione non e compilata in questa build')),
  );
  const BridgePrimaMano = lazy(() =>
    import.meta.env.VITE_FEATURE_BRIDGE_ENABLED === 'true'
      ? import('./bridge/BridgePrimaMano').then((m) => ({ default: m.BridgePrimaMano }))
      : Promise.reject(new Error('bridge: la sezione non e compilata in questa build')),
  );
  const BridgeLezioni = lazy(() =>
    import.meta.env.VITE_FEATURE_BRIDGE_ENABLED === 'true'
      ? import('./bridge/BridgeLezioni').then((m) => ({ default: m.BridgeLezioni }))
      : Promise.reject(new Error('bridge: la sezione non e compilata in questa build')),
  );
  const BridgeLezione = lazy(() =>
    import.meta.env.VITE_FEATURE_BRIDGE_ENABLED === 'true'
      ? import('./bridge/BridgeLezione').then((m) => ({ default: m.BridgeLezione }))
      : Promise.reject(new Error('bridge: la sezione non e compilata in questa build')),
  );
  const BridgePratica = lazy(() =>
    import.meta.env.VITE_FEATURE_BRIDGE_ENABLED === 'true'
      ? import('./bridge/BridgePratica').then((m) => ({ default: m.BridgePratica }))
      : Promise.reject(new Error('bridge: la sezione non e compilata in questa build')),
  );
  const BridgeMiniBridgeElenco = lazy(() =>
    import.meta.env.VITE_FEATURE_BRIDGE_ENABLED === 'true'
      ? import('./bridge/BridgePratica').then((m) => ({ default: m.BridgeMiniBridgeElenco }))
      : Promise.reject(new Error('bridge: la sezione non e compilata in questa build')),
  );
  const BridgeMiniBridge = lazy(() =>
    import.meta.env.VITE_FEATURE_BRIDGE_ENABLED === 'true'
      ? import('./bridge/BridgePratica').then((m) => ({ default: m.BridgeMiniBridge }))
      : Promise.reject(new Error('bridge: la sezione non e compilata in questa build')),
  );
  // K9b-1a «il tavolo che guarda» (condizioni 2 e 12 di tma-orcr per K9b-1): la pratica libera e il tavolo VERO (ticket via Limulo + socket del game
  // host). RETE => BETA: dietro `<RoleGate role="beta">`, MAI dietro `<CancelloBridge>` e mai in `ROTTE_APERTE` (l'ospite vede «Accedi»). Hanno
  // bisogno anche di Limulo acceso (il ticket passa dal BFF, come per gli scacchi e il burraco): senza, le rotte non esistono. Il confronto
  // LETTERALE del flag sta dentro il `lazy()`: nella build di produzione il file del socket e del tavolo non c'e.
  const BridgePraticaLibera = lazy(() =>
    import.meta.env.VITE_FEATURE_BRIDGE_ENABLED === 'true'
      ? import('./bridge/BridgePraticaLibera').then((m) => ({ default: m.BridgePraticaLibera }))
      : Promise.reject(new Error('bridge: la sezione non e compilata in questa build')),
  );
  const BridgeTavolo = lazy(() =>
    import.meta.env.VITE_FEATURE_BRIDGE_ENABLED === 'true'
      ? import('./bridge/BridgeTavolo').then((m) => ({ default: m.BridgeTavolo }))
      : Promise.reject(new Error('bridge: la sezione non e compilata in questa build')),
  );
  return [
    { path: 'bridge', element: <CancelloBridge>{lazyRoute(<BridgeIngresso />)}</CancelloBridge> },
    { path: 'bridge/prima-mano', element: <CancelloBridge>{lazyRoute(<BridgePrimaMano />)}</CancelloBridge> },
    { path: 'bridge/lezioni', element: <CancelloBridge>{lazyRoute(<BridgeLezioni />)}</CancelloBridge> },
    { path: 'bridge/lezioni/:id', element: <CancelloBridge>{lazyRoute(<BridgeLezione />)}</CancelloBridge> },
    { path: 'bridge/lezioni/:id/pratica/:mano', element: <CancelloBridge>{lazyRoute(<BridgePratica />)}</CancelloBridge> },
    { path: 'bridge/minibridge', element: <CancelloBridge>{lazyRoute(<BridgeMiniBridgeElenco />)}</CancelloBridge> },
    { path: 'bridge/minibridge/:mano', element: <CancelloBridge>{lazyRoute(<BridgeMiniBridge />)}</CancelloBridge> },
    { path: 'bridge/smazzata', element: <RoleGate role="beta">{lazyRoute(<BridgeDemo />)}</RoleGate> },
    ...(env.featureLimuloEnabled
      ? [
          { path: 'bridge/pratica', element: <RoleGate role="beta">{lazyRoute(<BridgePraticaLibera />)}</RoleGate> },
          { path: 'bridge/tavolo/:id', element: <RoleGate role="beta">{lazyRoute(<BridgeTavolo />)}</RoleGate> },
        ]
      : []),
  ];
}
const bridgeRoutes: RouteObject[] = env.featureBridgeEnabled ? bridgeRouteObjects() : [];

// QBurraco B5-1: `/burraco/verifica` (la verifica pubblica della mescolata) e `/burraco/tavolo` (il tavolo dell'impianto
// approvato, da FIXTURE). OPT-IN e acceso SOLO in `.env.staging` (`VITE_FEATURE_BURRACO_ENABLED`, `tools/verify-burraco.ts`),
// dietro `<RoleGate role="beta">`. NESSUNA voce di menu, Hub o selettore giochi: si aprono da URL. Le DUE letture del flag
// servono entrambe, come per `/bridge`: `env.featureBurracoEnabled` decide se le rotte ESISTONO (i test lo accendono col mock
// di `@/env`), il confronto LETTERALE dentro ogni `lazy()` e' quello che il bundler piega, cosi' in produzione l'`import()`
// sparisce e le pagine — con le loro fixture — non finiscono nella build.
function burracoRouteObjects(): RouteObject[] {
  const BurracoVerifica = lazy(() =>
    import.meta.env.VITE_FEATURE_BURRACO_ENABLED === 'true'
      ? import('./burraco/BurracoVerifica').then((m) => ({ default: m.BurracoVerifica }))
      : Promise.reject(new Error('burraco: la sezione non e compilata in questa build')),
  );
  const BurracoTavolo = lazy(() =>
    import.meta.env.VITE_FEATURE_BURRACO_ENABLED === 'true'
      ? import('./burraco/BurracoTavolo').then((m) => ({ default: m.BurracoTavolo }))
      : Promise.reject(new Error('burraco: la sezione non e compilata in questa build')),
  );
  // B5-2c: la pagina di verifica dell'AUDIT passo per passo (qlash-audit-v1): nessuna rete, il calcolo gira nel browser.
  const BurracoAudit = lazy(() =>
    import.meta.env.VITE_FEATURE_BURRACO_ENABLED === 'true'
      ? import('./burraco/BurracoAudit').then((m) => ({ default: m.BurracoAudit }))
      : Promise.reject(new Error('burraco: la sezione non e compilata in questa build')),
  );
  // B5-2b: il tavolo VERO. Ha bisogno anche di Limulo acceso (il ticket del socket passa dal BFF, come per gli scacchi con
  // `chessLimuloAcceso()`): senza, la rotta non esiste. Le fixture (`burraco/tavolo`) restano e restano dichiarate tali.
  const BurracoTavoloVero = lazy(() =>
    import.meta.env.VITE_FEATURE_BURRACO_ENABLED === 'true'
      ? import('./burraco/BurracoTavoloVero').then((m) => ({ default: m.BurracoTavoloVero }))
      : Promise.reject(new Error('burraco: la sezione non e compilata in questa build')),
  );
  return [
    {
      path: 'burraco/verifica',
      element: <RoleGate role="beta">{lazyRoute(<BurracoVerifica />)}</RoleGate>,
    },
    {
      path: 'burraco/tavolo',
      element: <RoleGate role="beta">{lazyRoute(<BurracoTavolo />)}</RoleGate>,
    },
    {
      path: 'burraco/audit',
      element: <RoleGate role="beta">{lazyRoute(<BurracoAudit />)}</RoleGate>,
    },
    ...(env.featureLimuloEnabled
      ? [
          {
            path: 'burraco/tavolo/:id',
            element: <RoleGate role="beta">{lazyRoute(<BurracoTavoloVero />)}</RoleGate>,
          },
        ]
      : []),
  ];
}
const burracoRoutes: RouteObject[] = env.featureBurracoEnabled ? burracoRouteObjects() : [];

// kit-giochi S4 (ADR-0235): la SCOPA e lo SCOPONE — `/scopa` (la Hub: Gioca, Invita un amico, Entra con un codice, I miei tavoli) e `/scopa/tavolo/:id` (la partita,
// socket del game host QScopa col ticket del BFF di Limulo). OPT-IN e acceso SOLO in `.env.staging` (`VITE_FEATURE_SCOPA_ENABLED`, `tools/verify-scopa.ts`), dietro
// `<RoleGate role="beta">` e solo con Limulo acceso (le sei rotte passano dal suo BFF). Le DUE letture servono entrambe, come per `/burraco`: `env.featureScopaEnabled`
// decide se le rotte ESISTONO, il confronto LETTERALE dentro ogni `lazy()` e' quello che il bundler piega (in produzione l'`import()` sparisce, e con lui le due pagine).
function scopaRouteObjects(): RouteObject[] {
  const ScopaHome = lazy(() =>
    import.meta.env.VITE_FEATURE_SCOPA_ENABLED === 'true'
      ? import('./scopa/ScopaHome').then((m) => ({ default: m.ScopaHome }))
      : Promise.reject(new Error('scopa: la sezione non e compilata in questa build')),
  );
  const ScopaPartita = lazy(() =>
    import.meta.env.VITE_FEATURE_SCOPA_ENABLED === 'true'
      ? import('./scopa/ScopaPartita').then((m) => ({ default: m.ScopaPartita }))
      : Promise.reject(new Error('scopa: la sezione non e compilata in questa build')),
  );
  return [
    {
      path: 'scopa',
      element: <RoleGate role="beta">{lazyRoute(<ScopaHome />)}</RoleGate>,
    },
    {
      path: 'scopa/tavolo/:id',
      element: <RoleGate role="beta">{lazyRoute(<ScopaPartita />)}</RoleGate>,
    },
  ];
}
const scopaRoutes: RouteObject[] = env.featureScopaEnabled && env.featureLimuloEnabled ? scopaRouteObjects() : [];

// kit-giochi P6 (ADR-0235): la BRISCOLA — `/briscola` (la Hub: Gioca, contro i robot, Invita un amico, Entra con un codice) e `/briscola/tavolo/:id` (la partita, socket del game
// host QBriscola col ticket del BFF di Limulo, dal trasporto comune `src/lib/gamehost/`). OPT-IN, dichiarato SOLO in `.env.staging` (`VITE_FEATURE_BRISCOLA_ENABLED`,
// `tools/verify-briscola.ts`), dietro `<RoleGate role="beta">` e solo con Limulo acceso. Le DUE letture servono entrambe, come per `/scopa`: `env.featureBriscolaEnabled` decide se le
// rotte ESISTONO, il confronto LETTERALE dentro ogni `lazy()` e' quello che il bundler piega (in produzione l'`import()` sparisce, e con lui le due pagine).
function briscolaRouteObjects(): RouteObject[] {
  const BriscolaHome = lazy(() =>
    import.meta.env.VITE_FEATURE_BRISCOLA_ENABLED === 'true'
      ? import('./briscola/BriscolaHome').then((m) => ({ default: m.BriscolaHome }))
      : Promise.reject(new Error('briscola: la sezione non e compilata in questa build')),
  );
  const BriscolaPartita = lazy(() =>
    import.meta.env.VITE_FEATURE_BRISCOLA_ENABLED === 'true'
      ? import('./briscola/BriscolaPartita').then((m) => ({ default: m.BriscolaPartita }))
      : Promise.reject(new Error('briscola: la sezione non e compilata in questa build')),
  );
  return [
    {
      path: 'briscola',
      element: <RoleGate role="beta">{lazyRoute(<BriscolaHome />)}</RoleGate>,
    },
    {
      path: 'briscola/tavolo/:id',
      element: <RoleGate role="beta">{lazyRoute(<BriscolaPartita />)}</RoleGate>,
    },
  ];
}
const briscolaRoutes: RouteObject[] = env.featureBriscolaEnabled && env.featureLimuloEnabled ? briscolaRouteObjects() : [];
// P6 del contratto del gin rummy (ADR-0235): il GIN RUMMY — `/ginrummy` (la Hub: Gioca, Invita un amico, Entra con un codice, I miei tavoli) e `/ginrummy/tavolo/:id` (la partita, socket del
// game host QGinRummy col ticket del BFF di Limulo, sul trasporto comune `lib/gamehost/`). OPT-IN e DICHIARATO solo in `.env.staging` (`VITE_FEATURE_GINRUMMY_ENABLED`, `tools/verify-ginrummy.ts`),
// dietro `<RoleGate role="beta">` e solo con Limulo acceso (le sei rotte passano dal suo BFF). Le DUE letture servono entrambe, come per `/scopa`: `env.featureGinRummyEnabled` decide se le
// rotte ESISTONO, il confronto LETTERALE dentro ogni `lazy()` e' quello che il bundler piega (in produzione l'`import()` sparisce, e con lui le due pagine).
function ginRummyRouteObjects(): RouteObject[] {
  const GinRummyHome = lazy(() =>
    import.meta.env.VITE_FEATURE_GINRUMMY_ENABLED === 'true'
      ? import('./ginrummy/GinRummyHome').then((m) => ({ default: m.GinRummyHome }))
      : Promise.reject(new Error('ginrummy: la sezione non e compilata in questa build')),
  );
  const GinRummyPartita = lazy(() =>
    import.meta.env.VITE_FEATURE_GINRUMMY_ENABLED === 'true'
      ? import('./ginrummy/GinRummyPartita').then((m) => ({ default: m.GinRummyPartita }))
      : Promise.reject(new Error('ginrummy: la sezione non e compilata in questa build')),
  );
  return [
    {
      path: 'ginrummy',
      element: <RoleGate role="beta">{lazyRoute(<GinRummyHome />)}</RoleGate>,
    },
    {
      path: 'ginrummy/tavolo/:id',
      element: <RoleGate role="beta">{lazyRoute(<GinRummyPartita />)}</RoleGate>,
    },
  ];
}
const ginRummyRoutes: RouteObject[] = env.featureGinRummyEnabled && env.featureLimuloEnabled ? ginRummyRouteObjects() : [];

// P6 del contratto della scala 40 (ADR-0235): la SCALA 40 — `/scala40` (la Hub: Gioca da 2 a 6 posti, Invita un amico, Entra con un codice, I miei tavoli) e `/scala40/tavolo/:id` (la partita, socket del
// game host QScala40 col ticket del BFF di Limulo, sul trasporto comune `lib/gamehost/` e sul tavolo da carte da 2 a 6 posti di `components/tavolo/`). OPT-IN e DICHIARATO solo in `.env.staging`
// (`VITE_FEATURE_SCALA40_ENABLED`, `tools/verify-scala40.ts`), dietro `<RoleGate role="beta">` e solo con Limulo acceso (le sei rotte passano dal suo BFF). Le DUE letture servono entrambe, come per `/ginrummy`:
// `env.featureScala40Enabled` decide se le rotte ESISTONO (i test lo accendono col mock di `@/env`), il confronto LETTERALE dentro il `lazy()` e' quello che il bundler piega, cosi' in produzione le pagine non entrano.
function scala40RouteObjects(): RouteObject[] {
  const Scala40Home = lazy(() =>
    import.meta.env.VITE_FEATURE_SCALA40_ENABLED === 'true'
      ? import('./scala40/Scala40Home').then((m) => ({ default: m.Scala40Home }))
      : Promise.reject(new Error('scala40: la sezione non e compilata in questa build')),
  );
  const Scala40Partita = lazy(() =>
    import.meta.env.VITE_FEATURE_SCALA40_ENABLED === 'true'
      ? import('./scala40/Scala40Partita').then((m) => ({ default: m.Scala40Partita }))
      : Promise.reject(new Error('scala40: la sezione non e compilata in questa build')),
  );
  return [
    {
      path: 'scala40',
      element: <RoleGate role="beta">{lazyRoute(<Scala40Home />)}</RoleGate>,
    },
    {
      path: 'scala40/tavolo/:id',
      element: <RoleGate role="beta">{lazyRoute(<Scala40Partita />)}</RoleGate>,
    },
  ];
}
const scala40Routes: RouteObject[] = env.featureScala40Enabled && env.featureLimuloEnabled ? scala40RouteObjects() : [];

// P6 del contratto del sette e mezzo (ADR-0235): il SETTE E MEZZO — `/settemezzo` (la Hub: Gioca, Contro il robot, Invita un amico, Entra con un codice, I miei tavoli) e `/settemezzo/tavolo/:id` (la partita,
// socket del game host QSetteMezzo col ticket del BFF di Limulo, sul trasporto comune `lib/gamehost/`). OPT-IN e DICHIARATO solo in `.env.staging` (`VITE_FEATURE_SETTEMEZZO_ENABLED`, `tools/verify-settemezzo.ts`),
// dietro `<RoleGate role="beta">` e solo con Limulo acceso (le sei rotte passano dal suo BFF). Le DUE letture servono entrambe, come per `/ginrummy`: `env.featureSettemezzoEnabled` decide se le rotte ESISTONO
// (i test lo accendono col mock di `@/env`), il confronto LETTERALE dentro il `lazy()` e' quello che il bundler piega, cosi' in produzione le pagine non entrano.
function settemezzoRouteObjects(): RouteObject[] {
  const SettemezzoHome = lazy(() =>
    import.meta.env.VITE_FEATURE_SETTEMEZZO_ENABLED === 'true'
      ? import('./settemezzo/SettemezzoHome').then((m) => ({ default: m.SettemezzoHome }))
      : Promise.reject(new Error('settemezzo: la sezione non e compilata in questa build')),
  );
  const SettemezzoPartita = lazy(() =>
    import.meta.env.VITE_FEATURE_SETTEMEZZO_ENABLED === 'true'
      ? import('./settemezzo/SettemezzoPartita').then((m) => ({ default: m.SettemezzoPartita }))
      : Promise.reject(new Error('settemezzo: la sezione non e compilata in questa build')),
  );
  return [
    {
      path: 'settemezzo',
      element: <RoleGate role="beta">{lazyRoute(<SettemezzoHome />)}</RoleGate>,
    },
    {
      path: 'settemezzo/tavolo/:id',
      element: <RoleGate role="beta">{lazyRoute(<SettemezzoPartita />)}</RoleGate>,
    },
  ];
}
const settemezzoRoutes: RouteObject[] = env.featureSettemezzoEnabled && env.featureLimuloEnabled ? settemezzoRouteObjects() : [];

// P6 del contratto del tressette (ADR-0235): il TRESSETTE — `/tressette` (la Hub: Gioca, Contro i robot, Invita un amico, Entra con un codice, I miei tavoli) e `/tressette/tavolo/:id` (la partita, socket del game
// host QTressette col ticket del BFF di Limulo, sul trasporto comune `lib/gamehost/`). OPT-IN e DICHIARATO solo in `.env.staging` (`VITE_FEATURE_TRESSETTE_ENABLED`, `tools/verify-tressette.ts`), dietro
// `<RoleGate role="beta">` e solo con Limulo acceso (le sei rotte passano dal suo BFF). Le DUE letture servono entrambe, come per `/settemezzo`: `env.featureTressetteEnabled` decide se le rotte ESISTONO
// (i test lo accendono col mock di `@/env`), il confronto LETTERALE dentro il `lazy()` e' quello che il bundler piega, cosi' in produzione le pagine non entrano.
function tressetteRouteObjects(): RouteObject[] {
  const TressetteHome = lazy(() =>
    import.meta.env.VITE_FEATURE_TRESSETTE_ENABLED === 'true'
      ? import('./tressette/TressetteHome').then((m) => ({ default: m.TressetteHome }))
      : Promise.reject(new Error('tressette: la sezione non e compilata in questa build')),
  );
  const TressettePartita = lazy(() =>
    import.meta.env.VITE_FEATURE_TRESSETTE_ENABLED === 'true'
      ? import('./tressette/TressettePartita').then((m) => ({ default: m.TressettePartita }))
      : Promise.reject(new Error('tressette: la sezione non e compilata in questa build')),
  );
  return [
    {
      path: 'tressette',
      element: <RoleGate role="beta">{lazyRoute(<TressetteHome />)}</RoleGate>,
    },
    {
      path: 'tressette/tavolo/:id',
      element: <RoleGate role="beta">{lazyRoute(<TressettePartita />)}</RoleGate>,
    },
  ];
}
const tressetteRoutes: RouteObject[] = env.featureTressetteEnabled && env.featureLimuloEnabled ? tressetteRouteObjects() : [];

// Il SOLITARIO (TMA PR 1-2 di 3, contratto C17; sezione «Solitari», S10): `/solitario` (l'HUB: una tessera per variante), `/solitario/klondike` (il Klondike: i tre ingressi e le classifiche), `/solitario/tavolo/:id` (un tavolo, asincrono o dal vivo) e
// `/solitario/sessione/:id` (la smazzata del giorno, o una sessione singola). OPT-IN e acceso SOLO in `.env.staging` (`VITE_FEATURE_SOLITARIO_ENABLED`, `tools/verify-solitario.ts`), dietro
// `<RoleGate role="beta">` e solo con Limulo acceso. Le DUE letture servono entrambe, come per `/burraco`: `env.featureSolitarioEnabled` decide se le rotte ESISTONO, il confronto LETTERALE dentro ogni
// `lazy()` e' quello che il bundler piega (in produzione l'`import()` sparisce, e con lui le pagine: nessun chunk del solitario nella build).
function solitarioRouteObjects(): RouteObject[] {
  const SolitarioHome = lazy(() =>
    import.meta.env.VITE_FEATURE_SOLITARIO_ENABLED === 'true'
      ? import('./solitario/SolitarioHome').then((m) => ({ default: m.SolitarioHome }))
      : Promise.reject(new Error('solitario: la sezione non e compilata in questa build')),
  );
  const SolitarioKlondike = lazy(() =>
    import.meta.env.VITE_FEATURE_SOLITARIO_ENABLED === 'true'
      ? import('./solitario/SolitarioKlondike').then((m) => ({ default: m.SolitarioKlondike }))
      : Promise.reject(new Error('solitario: la sezione non e compilata in questa build')),
  );
  const SolitarioTavolo = lazy(() =>
    import.meta.env.VITE_FEATURE_SOLITARIO_ENABLED === 'true'
      ? import('./solitario/SolitarioTavolo').then((m) => ({ default: m.SolitarioTavolo }))
      : Promise.reject(new Error('solitario: la sezione non e compilata in questa build')),
  );
  const SolitarioSessione = lazy(() =>
    import.meta.env.VITE_FEATURE_SOLITARIO_ENABLED === 'true'
      ? import('./solitario/SolitarioSessione').then((m) => ({ default: m.SolitarioSessione }))
      : Promise.reject(new Error('solitario: la sezione non e compilata in questa build')),
  );
  // ROTTA DI PROVA (qsolitario-orcr-48-spider-prova, contratto «Solitari» P4/S9): la tavola statica dello Spider a 10 colonne. SI TOGLIE nella PR del motore dello Spider.
  const SolitarioSpiderProva = lazy(() =>
    import.meta.env.VITE_FEATURE_SOLITARIO_ENABLED === 'true'
      ? import('./solitario/SolitarioSpiderProva').then((m) => ({ default: m.SolitarioSpiderProva }))
      : Promise.reject(new Error('solitario: la sezione non e compilata in questa build')),
  );
  // ROTTA DI PROVA (qsolitario-orcr-59-banco, contratto «Solitari» P3.4a/S9): la tavola statica del TriPeaks, con la riga di fondo da 10 carte. SI TOGLIE nella PR del tavolo del TriPeaks (P3.4b).
  // Sta DENTRO `solitarioRouteObjects()`: il predicato che la rende possibile e` quello delle altre rotte del solitario (flag E Limulo, `solitarioRoutes` sotto), letto in UN punto; nessun helper, nessuna seconda lettura.
  const SolitarioTripeaksProva = lazy(() =>
    import.meta.env.VITE_FEATURE_SOLITARIO_ENABLED === 'true'
      ? import('./solitario/SolitarioTripeaksProva').then((m) => ({ default: m.SolitarioTripeaksProva }))
      : Promise.reject(new Error('solitario: la sezione non e compilata in questa build')),
  );
  return [
    {
      path: 'solitario',
      element: <RoleGate role="beta">{lazyRoute(<SolitarioHome />)}</RoleGate>,
    },
    {
      path: 'solitario/klondike',
      element: <RoleGate role="beta">{lazyRoute(<SolitarioKlondike />)}</RoleGate>,
    },
    {
      // ROTTA DI PROVA: si toglie nella PR del motore dello Spider (la tavola statica a 10 colonne, L-3812 (b))
      path: 'solitario/spider-prova',
      element: <RoleGate role="beta">{lazyRoute(<SolitarioSpiderProva />)}</RoleGate>,
    },
    {
      // ROTTA DI PROVA: si toglie nella PR del tavolo del TriPeaks (P3.4b) (la tavola statica della riga di fondo da 10, S9)
      path: 'solitario/tripeaks-prova',
      element: <RoleGate role="beta">{lazyRoute(<SolitarioTripeaksProva />)}</RoleGate>,
    },
    {
      path: 'solitario/tavolo/:id',
      element: <RoleGate role="beta">{lazyRoute(<SolitarioTavolo />)}</RoleGate>,
    },
    {
      path: 'solitario/sessione/:id',
      element: <RoleGate role="beta">{lazyRoute(<SolitarioSessione />)}</RoleGate>,
    },
  ];
}
const solitarioRoutes: RouteObject[] = env.featureSolitarioEnabled && env.featureLimuloEnabled ? solitarioRouteObjects() : [];

// Il 2048 (contratto C21; cantiere q2048-orcr): `/2048`. SENZA Limulo resta la ANTEPRIMA DI COLLAUDO di sempre (gioco in locale, nessuna rete). CON Limulo (`env.featureLimuloEnabled`) `/2048` e la sezione vera sul profilo a punteggio:
// la Hub (sfida del giorno, tavoli asincroni e dal vivo da 2 o da 4, classifiche), `/2048/sessione/:id` e `/2048/tavolo/:id`; l'anteprima resta raggiungibile dalla Hub come «Pratica (non conta)» su `/2048/pratica`.
// OPT-IN e acceso SOLO in `.env.staging` (`VITE_FEATURE_2048_ENABLED`, `tools/verify-2048.ts`), dietro `<RoleGate role="beta">`. Le DUE letture servono entrambe: `env.feature2048Enabled`
// decide se le rotte ESISTONO (i test lo accendono col mock di `@/env`), il confronto LETTERALE dentro ogni `lazy()` e' quello che il bundler piega (in produzione gli `import()` spariscono).
function q2048RouteObjects(): RouteObject[] {
  const Q2048Home = lazy(() =>
    import.meta.env.VITE_FEATURE_2048_ENABLED === 'true'
      ? import('./q2048/Q2048Home').then((m) => ({ default: m.Q2048Home }))
      : Promise.reject(new Error('2048: la sezione non e compilata in questa build')),
  );
  if (!env.featureLimuloEnabled) {
    return [
      {
        path: '2048',
        element: <RoleGate role="beta">{lazyRoute(<Q2048Home />)}</RoleGate>,
      },
    ];
  }
  const Q2048Pagina = lazy(() =>
    import.meta.env.VITE_FEATURE_2048_ENABLED === 'true'
      ? import('./q2048/Q2048Pagina').then((m) => ({ default: m.Q2048Pagina }))
      : Promise.reject(new Error('2048: la sezione non e compilata in questa build')),
  );
  const Q2048Sessione = lazy(() =>
    import.meta.env.VITE_FEATURE_2048_ENABLED === 'true'
      ? import('./q2048/Q2048Sessione').then((m) => ({ default: m.Q2048Sessione }))
      : Promise.reject(new Error('2048: la sezione non e compilata in questa build')),
  );
  const Q2048Tavolo = lazy(() =>
    import.meta.env.VITE_FEATURE_2048_ENABLED === 'true'
      ? import('./q2048/Q2048Tavolo').then((m) => ({ default: m.Q2048Tavolo }))
      : Promise.reject(new Error('2048: la sezione non e compilata in questa build')),
  );
  return [
    { path: '2048', element: <RoleGate role="beta">{lazyRoute(<Q2048Pagina />)}</RoleGate> },
    { path: '2048/pratica', element: <RoleGate role="beta">{lazyRoute(<Q2048Home />)}</RoleGate> },
    { path: '2048/sessione/:id', element: <RoleGate role="beta">{lazyRoute(<Q2048Sessione />)}</RoleGate> },
    { path: '2048/tavolo/:id', element: <RoleGate role="beta">{lazyRoute(<Q2048Tavolo />)}</RoleGate> },
  ];
}
const q2048Routes: RouteObject[] = env.feature2048Enabled ? q2048RouteObjects() : [];

// La SONDA DI CATTURA (tma-orcr-95, solo staging): `/settings/sonda-cattura`, dietro `beta`. `PaginaSondaCattura` e' `null` in produzione (flag letterale in
// `lib/sondaCattura.ts`), quindi la rotta non esiste e l'URL cade nel catch-all. Blocco a se', lontano dalle rotte dei minigiochi.
const sondaCatturaRoutes: RouteObject[] = PaginaSondaCattura
  ? [{ path: 'settings/sonda-cattura', element: <RoleGate role="beta">{lazyRoute(<PaginaSondaCattura />)}</RoleGate> }]
  : [];

const chessRoutes: RouteObject[] = env.featureChessEnabled
  ? [
      { path: 'chess', element: lazyRoute(<Chess />) },
      { path: 'chess/tournaments', element: lazyRoute(<ChessTournaments />) },
      { path: 'chess/tournaments/:id', element: lazyRoute(<ChessTournamentDetail />) },
      { path: 'chess/brackets', element: lazyRoute(<ChessBrackets />) },
      { path: 'chess/brackets/:id', element: lazyRoute(<ChessBracketDetail />) },
      { path: 'chess/challenges', element: lazyRoute(<ChessChallenges />) },
      { path: 'chess/challenges/:id', element: lazyRoute(<ChessChallengeDetail />) },
      { path: 'chess/leaderboard', element: lazyRoute(<ChessLeaderboard />) },
    ]
  : [];

const cocRoutes: RouteObject[] = env.featureCocEnabled
  ? [
      { path: 'coc', element: lazyRoute(<CocHome />) },
      { path: 'coc/link', element: lazyRoute(<CocLink />) },
      { path: 'coc/profile', element: lazyRoute(<CocProfile />) },
      { path: 'coc/clan', element: lazyRoute(<CocClan />) },
      { path: 'coc/competitions', element: lazyRoute(<CocCompetitions />) },
      { path: 'coc/competitions/:id', element: lazyRoute(<CocCompetitionDetail />) },
      { path: 'coc/coach', element: lazyRoute(<CocCoach />) },
    ]
  : [];

// Sotto-sezione Missioni (Quest) — dietro `featureMissionsEnabled`, OPT-IN: nasce
// spenta ovunque, PROD compresa. UNA rotta e basta: qui c'e' la sola lista, il
// claim e' fase 2 e non ha una schermata propria.
//
// ⚠️ STA SOTTO `settings/`, e la vecchia `/missioni` di primo livello NON resta
// accanto: e' una sotto-sezione del blocco IMPOSTAZIONI del Profilo (ordine di
// Luca, 2026-08-30). Due porte per la stessa cosa e' esattamente il problema che
// il consolidamento del 2026-07-24 ha chiuso — `/settings` stesso e' un redirect
// al Profilo proprio per non averne due.
//
// Il nome dice COSA contiene, non quale servizio la serve: le vicine sono
// `settings/wallet` (servita da QBank) e `settings/accounts`, non
// `settings/qbank`. E `missions`, mai `challenges`: in questo repo `challenges`
// significa gia' le Sfide di QDuels (`locales/*/challenges.json`).
// ── Limulo ────────────────────────────────────────────────────────────────
// La sezione servita dal BFF `app-qlash-api` (ADR-0078). Lazy come le altre: chi non
// ha il flag e il ruolo non ne scarica nemmeno il codice.
const LimuloTournaments = lazy(() =>
  import('./limulo/LimuloTournaments').then((m) => ({ default: m.LimuloTournaments })),
);
const LimuloTournamentDetail = lazy(() =>
  import('./limulo/LimuloTournamentDetail').then((m) => ({ default: m.LimuloTournamentDetail })),
);
const LimuloStorico = lazy(() =>
  import('./limulo/LimuloStorico').then((m) => ({ default: m.LimuloStorico })),
);

const LimuloMine = lazy(() =>
  import('./limulo/LimuloMine').then((m) => ({ default: m.LimuloMine })),
);
const LimuloDiagnostics = lazy(() =>
  import('./limulo/LimuloDiagnostics').then((m) => ({ default: m.LimuloDiagnostics })),
);
const LimuloMatches = lazy(() =>
  import('./limulo/LimuloMatches').then((m) => ({ default: m.LimuloMatches })),
);
const LimuloMatchDetail = lazy(() =>
  import('./limulo/LimuloMatchDetail').then((m) => ({ default: m.LimuloMatchDetail })),
);
const LimuloProfiles = lazy(() =>
  import('./limulo/LimuloProfiles').then((m) => ({ default: m.LimuloProfiles })),
);
const LimuloProfileCreate = lazy(() =>
  import('./limulo/LimuloProfileCreate').then((m) => ({ default: m.LimuloProfileCreate })),
);

/**
 * Sezione Limulo — DUE serrature indipendenti, e servono entrambe.
 *
 *  1. `env.featureLimuloEnabled` e' compile-time e OPT-IN (`=== 'true'`): senza, queste
 *     rotte non esistono proprio. Deve essere opt-in perche' oggi ogni rotta dati del BFF
 *     risponde 403 — accesa di default, la sezione comparirebbe rotta ovunque.
 *  2. `<RoleGate role="beta">` e' feature-level e gira a runtime sul ruolo Logto.
 *
 * ⚠️ I PATH STANNO TUTTI SOTTO `limulo/`, e non e' una preferenza estetica.
 * `createBrowserRouter` risolve per RANK di specificita', non per ordine di dichiarazione:
 * mettere queste rotte in fondo all'array non protegge da niente. Davanti a un path
 * DUPLICATO ESATTO vince chi e' dichiarato prima — senza errori, senza warning, con la
 * build verde. E su questo router vivono gia' `tournaments`, `tournaments/:id`,
 * `tournaments/:id/watch-party` e `teams/:id`, che sono la superficie tornei del monolite
 * Laravel ATTIVA in produzione: una rotta nuda le metterebbe in ombra, e siccome le nostre
 * stanno dietro il RoleGate l'ombra la vedrebbero SOLO gli utenti beta.
 *
 * I path vengono da `lib/limulo/routes.ts`, dove `tools/verify-limulo.ts` verifica il
 * prefisso: la regola e' meccanica invece che ricordata.
 */
const limuloRoutes: RouteObject[] = env.featureLimuloEnabled
  ? [
      {
        path: LIMULO_PATHS.tournaments,
        element: <RoleGate role="beta">{lazyRoute(<LimuloTournaments />)}</RoleGate>,
      },
      {
        path: LIMULO_PATHS.tournamentDetail,
        element: <RoleGate role="beta">{lazyRoute(<LimuloTournamentDetail />)}</RoleGate>,
      },
      {
        /*
         * 🔴 **QUESTA ROTTA NON HA UNA SCHERMATA SUA: E' UN RIMANDO AL TAB.**
         * Decisione del fronte di disegno, 2026-09-20, dopo che il cancello di #116 ha
         * segnalato che il path era rimasto vivo e senza nessuna porta — la scheda torneo e'
         * passata ai tab e la classifica ha smesso di essere una destinazione.
         *
         * Le tre vie scartate, e il perche':
         *  · **ritirarla** romperebbe un collegamento profondo che un beta puo' avere gia' in
         *    mano, ed e' l'unico modo di condividere una classifica;
         *  · **lasciarla com'era** e' l'orfanaggio che questo giro esiste per chiudere;
         *  · **una seconda schermata** sarebbero due implementazioni della stessa classifica,
         *    che divergono al primo cambiamento del contratto.
         * ⇒ apre la scheda col tab giusto gia' selezionato. **Una implementazione sola.**
         */
        path: LIMULO_PATHS.standings,
        element: (
          <RoleGate role="beta">{lazyRoute(<LimuloTournamentDetail tab="classifica" />)}</RoleGate>
        ),
      },
      // La v3 (fase 3, PR 2): le schermate aperte dai riquadri della pagina del torneo. Il percorso sta QUI e non in `LIMULO_ROUTE_PATHS` (che indicizza per posizione e finirebbe in produzione):
      // il confronto LETTERALE del flag sta in `lib/v3.ts` (`V3_IN_BUILD`, `VistaTorneoV3`), in produzione la rotta non esiste e il suo pezzo non e' nel bundle. Dove esiste ma la v3 non
      // e' accesa, la schermata rimanda con `replace` alla pagina del torneo.
      ...(V3_IN_BUILD && VistaTorneoV3
        ? [{ path: `${LIMULO_ROUTE_PREFIX}/tournaments/:id/vista/:quale`, element: <RoleGate role="beta">{lazyRoute(<VistaTorneoV3 />)}</RoleGate> }]
        : []),
      {
        path: LIMULO_PATHS.history,
        element: <RoleGate role="beta">{lazyRoute(<LimuloStorico />)}</RoleGate>,
      },
      {
        path: LIMULO_PATHS.mine,
        element: <RoleGate role="beta">{lazyRoute(<LimuloMine />)}</RoleGate>,
      },
      {
        path: LIMULO_PATHS.diagnostics,
        element: <RoleGate role="beta">{lazyRoute(<LimuloDiagnostics />)}</RoleGate>,
      },
      {
        path: LIMULO_PATHS.matches,
        element: <RoleGate role="beta">{lazyRoute(<LimuloMatches />)}</RoleGate>,
      },
      {
        path: LIMULO_PATHS.matchDetail,
        element: <RoleGate role="beta">{lazyRoute(<LimuloMatchDetail />)}</RoleGate>,
      },
      {
        path: LIMULO_PATHS.profiles,
        element: <RoleGate role="beta">{lazyRoute(<LimuloProfiles />)}</RoleGate>,
      },
      {
        path: LIMULO_PATHS.profileCreate,
        element: <RoleGate role="beta">{lazyRoute(<LimuloProfileCreate />)}</RoleGate>,
      },
    ]
  : [];

const missionsRoutes: RouteObject[] = env.featureMissionsEnabled
  ? [{ path: 'settings/missions', element: lazyRoute(<Missions />) }]
  : [];

// Backgammon — DUE serrature, e servono entrambe. `featureBackgammonEnabled` e'
// compile-time: senza, queste rotte non esistono. `<RoleGate role="beta">` e'
// feature-level e gira a runtime sul ruolo Logto.
//
// ⚠️ Il flag da solo NON basta a limitare la sezione ai beta, ed e' il motivo per
// cui questo gate esiste. Il flag e' GLOBALE: accenderlo la rendeva raggiungibile
// da chiunque, e un utente col ruolo `user` porta ZERO scope sulla resource
// QGammon — quindi la sezione gli si sarebbe rotta in mano, senza nemmeno il
// dev-bypass a coprire (in produzione e' una stringa vuota letterale, per
// costruzione). Il gate va su entrambe le vie d'accesso: qui e sull'ingresso
// nell'Hub (`components/hub/HubSections.tsx`) — una rotta protetta con una tile
// visibile e' un vicolo cieco, non una protezione.
//
// Stessa forma della sezione Limulo piu' sotto: nessun `fallback`, quindi un
// utente non-beta che arriva qui per deep-link vede un'area vuota. E' il
// comportamento del precedente, non una scelta nuova — se si vuole un rimando
// alla Home e' una decisione da prendere per ENTRAMBE le sezioni insieme.
const backgammonRoutes: RouteObject[] = env.featureBackgammonEnabled
  ? [
      {
        path: 'backgammon',
        element: <RoleGate role="beta">{lazyRoute(<BackgammonHome />)}</RoleGate>,
      },
      {
        path: 'backgammon/match/:id',
        element: <RoleGate role="beta">{lazyRoute(<BackgammonMatch />)}</RoleGate>,
      },
      {
        path: 'backgammon/replay/:id',
        element: <RoleGate role="beta">{lazyRoute(<BackgammonReplay />)}</RoleGate>,
      },
      // Guardare una partita in corso, in sola lettura (scheda «Live», Luca I-208). Solo col suo
      // flag, come la scheda: una rotta raggiungibile senza la scheda che ci porta sarebbe un
      // ingresso che il flag non chiude.
      ...(env.featureBackgammonLiveEnabled
        ? [
            {
              path: 'backgammon/live/:id',
              element: <RoleGate role="beta">{lazyRoute(<BackgammonWatch />)}</RoleGate>,
            },
          ]
        : []),
    ]
  : [];

// QChinesePoker (QCP) — Pineapple OFC + Fantasyland live match: `/chinesepoker`, `match/:id`, `result/:id`, `replay/:id`.
// OPT-IN e SPENTO OVUNQUE finche' non arriva T2 (`VITE_FEATURE_CP_ENABLED`, `tools/verify-qcp.ts`): dietro `<RoleGate role="beta">`,
// con la voce nella Hub e nel selettore giochi solo per chi e' beta. Le DUE letture del flag servono entrambe, come per `/burraco`:
// `qcpAcceso()` decide se le rotte ESISTONO (i test lo accendono col mock di `@/env`), il confronto LETTERALE dentro ogni `lazy()`
// e' quello che il bundler piega, cosi' nel bundle spento l'`import()` sparisce e con lui le quattro pagine.
function chinesePokerRouteObjects(): RouteObject[] {
  const ChinesePokerHome = lazy(() =>
    import.meta.env.VITE_FEATURE_CP_ENABLED === 'true'
      ? import('./chinesepoker/ChinesePokerHome').then((m) => ({ default: m.ChinesePokerHome }))
      : Promise.reject(new Error('chinesepoker: la sezione non e compilata in questa build')),
  );
  const ChinesePokerMatch = lazy(() =>
    import.meta.env.VITE_FEATURE_CP_ENABLED === 'true'
      ? import('./chinesepoker/ChinesePokerMatch').then((m) => ({ default: m.ChinesePokerMatch }))
      : Promise.reject(new Error('chinesepoker: la sezione non e compilata in questa build')),
  );
  // T11: result screen — card.png + win/loss header + Telegram share.
  const ChinesePokerResult = lazy(() =>
    import.meta.env.VITE_FEATURE_CP_ENABLED === 'true'
      ? import('./chinesepoker/ChinesePokerResult').then((m) => ({ default: m.ChinesePokerResult }))
      : Promise.reject(new Error('chinesepoker: la sezione non e compilata in questa build')),
  );
  // T6: per-hand replay viewer — final boards + discards + net per hand.
  const ChinesePokerReplay = lazy(() =>
    import.meta.env.VITE_FEATURE_CP_ENABLED === 'true'
      ? import('./chinesepoker/ChinesePokerReplay').then((m) => ({ default: m.ChinesePokerReplay }))
      : Promise.reject(new Error('chinesepoker: la sezione non e compilata in questa build')),
  );
  return [
    { path: 'chinesepoker', element: <RoleGate role="beta">{lazyRoute(<ChinesePokerHome />)}</RoleGate> },
    { path: 'chinesepoker/match/:id', element: <RoleGate role="beta">{lazyRoute(<ChinesePokerMatch />)}</RoleGate> },
    { path: 'chinesepoker/result/:id', element: <RoleGate role="beta">{lazyRoute(<ChinesePokerResult />)}</RoleGate> },
    { path: 'chinesepoker/replay/:id', element: <RoleGate role="beta">{lazyRoute(<ChinesePokerReplay />)}</RoleGate> },
  ];
}
const chinesePokerRoutes: RouteObject[] = qcpAcceso() ? chinesePokerRouteObjects() : [];

// Il BIRIBA (Μπιρίμπα, «Mpirimpa»; ADR-0265, contratto del fronte I-1296) — PR-A, lo SCHELETRO: `/mpirimpa` (la pagina); PR-B: `/mpirimpa/tavolo/:id` (il tavolo). BLOCCO A SE', una
// costante e uno spread (`...mpirimpaRoutes`, dopo `...pilottaRoutes` e prima del catch-all). OPT-IN e DICHIARATO solo in `.env.staging` (`VITE_FEATURE_MPIRIMPA_ENABLED`, `tools/verify-mpirimpa.ts`), dietro
// `<RoleGate role="beta">` e solo con Limulo acceso. Le DUE letture servono entrambe, come per `/pilotta`: `env.featureMpirimpaEnabled` decide se la rotta ESISTE (i test lo accendono col mock di `@/env`), il confronto
// LETTERALE dentro il `lazy()` e' quello che il bundler piega, cosi' in produzione la pagina non entra. Sta PRIMA del blocco della Pilotta perche' `tests/pilotta-rotte-flag.test.ts` pretende che la costante della
// Pilotta sia l'ultima riga prima della creazione del router.
function mpirimpaRouteObjects(): RouteObject[] {
  const MpirimpaHome = lazy(() =>
    import.meta.env.VITE_FEATURE_MPIRIMPA_ENABLED === 'true'
      ? import('./mpirimpa/MpirimpaHome').then((m) => ({ default: m.MpirimpaHome }))
      : Promise.reject(new Error('mpirimpa: la sezione non e compilata in questa build')),
  );
  // PR-B: il TAVOLO, con lo stesso confronto letterale nel SUO `lazy()` (il bundler lo piega anche qui)
  const MpirimpaPartita = lazy(() =>
    import.meta.env.VITE_FEATURE_MPIRIMPA_ENABLED === 'true'
      ? import('./mpirimpa/MpirimpaPartita').then((m) => ({ default: m.MpirimpaPartita }))
      : Promise.reject(new Error('mpirimpa: la sezione non e compilata in questa build')),
  );
  return [
    {
      path: 'mpirimpa',
      element: <RoleGate role="beta">{lazyRoute(<MpirimpaHome />)}</RoleGate>,
    },
    {
      path: 'mpirimpa/tavolo/:id',
      element: <RoleGate role="beta">{lazyRoute(<MpirimpaPartita />)}</RoleGate>,
    },
  ];
}
const mpirimpaRoutes: RouteObject[] = env.featureMpirimpaEnabled && env.featureLimuloEnabled ? mpirimpaRouteObjects() : [];

// P8 del contratto della Pilotta (ADR-0235): la PILOTTA — `/pilotta` (la Hub: il traguardo, Gioca, Contro i robot, Invita un amico, Entra con un codice, I miei tavoli) e `/pilotta/tavolo/:id` (la partita, socket del
// game host QPilotta col ticket del BFF di Limulo, sul trasporto comune `lib/gamehost/`). BLOCCO A SE', una costante e uno spread (`...pilottaRoutes`, prima del catch-all), lontano dalle rotte degli altri giochi
// (richiesta di tma-orcr, 31/10 03:23Z). OPT-IN e DICHIARATO solo in `.env.staging` (`VITE_FEATURE_PILOTTA_ENABLED`, `tools/verify-pilotta.ts`), dietro `<RoleGate role="beta">` e solo con Limulo acceso. Le DUE
// letture servono entrambe, come per `/tressette`: `env.featurePilottaEnabled` decide se le rotte ESISTONO (i test lo accendono col mock di `@/env`), il confronto LETTERALE dentro il `lazy()` e' quello che il
// bundler piega, cosi' in produzione le pagine non entrano.
function pilottaRouteObjects(): RouteObject[] {
  const PilottaHome = lazy(() =>
    import.meta.env.VITE_FEATURE_PILOTTA_ENABLED === 'true'
      ? import('./pilotta/PilottaHome').then((m) => ({ default: m.PilottaHome }))
      : Promise.reject(new Error('pilotta: la sezione non e compilata in questa build')),
  );
  const PilottaPartita = lazy(() =>
    import.meta.env.VITE_FEATURE_PILOTTA_ENABLED === 'true'
      ? import('./pilotta/PilottaPartita').then((m) => ({ default: m.PilottaPartita }))
      : Promise.reject(new Error('pilotta: la sezione non e compilata in questa build')),
  );
  return [
    {
      path: 'pilotta',
      element: <RoleGate role="beta">{lazyRoute(<PilottaHome />)}</RoleGate>,
    },
    {
      path: 'pilotta/tavolo/:id',
      element: <RoleGate role="beta">{lazyRoute(<PilottaPartita />)}</RoleGate>,
    },
  ];
}
const pilottaRoutes: RouteObject[] = env.featurePilottaEnabled && env.featureLimuloEnabled ? pilottaRouteObjects() : [];

export const router = createBrowserRouter([
  // Public route: must NOT be wrapped in AppShell (no auth gating, no TabBar)
  // — AppShell would loop the user back to <Login /> before the OIDC code is
  // exchanged for tokens.
  { path: '/auth/callback', element: <AuthCallback /> },

  // Avatar Editor wizard runs full-screen — bypass AppShell so the TabBar
  // doesn't intercept clicks on the sticky footer Next/Back buttons.
  ...(env.featureAvatarsEnabled
    ? [
        {
          path: '/avatars/editor',
          // I-416: era montato senza nessuna guardia. L'editor genera (animate-2d): con il flag Studio acceso, solo
          // `administrator`.
          element: soloCreatoriConStudio(lazyRoute(<AvatarEditor />)),
          errorElement: <RouteErrorBoundary />,
        },
      ]
    : []),

  {
    path: '/',
    element: <AppShell />,
    // Copre ogni rotta lazy sotto l'AppShell (tutte le schermate di gioco): un import di
    // chunk fallito qui rimpiazza la pagina di default di React Router — che PERO' e'
    // l'AppShell intera (TabBar compresa), non solo l'`<Outlet/>`: `errorElement` sostituisce
    // l'intero `element` della rotta a cui e' attaccato, non solo i suoi children. Sul chunk
    // vecchio va bene cosi': la guardia ricarica prima che l'utente veda altro; sull'errore
    // generico e' il prezzo di avere UN errorElement solo per tutto l'albero — v.
    // RouteErrorBoundary.tsx.
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: <Hub /> },

      { path: 'tournaments', element: <Tournaments /> },
      { path: 'tournaments/:id', element: <TournamentDetail /> },
      { path: 'tournaments/:id/watch-party', element: <WatchParty /> },

      { path: 'matches/:id', element: <MatchDetail /> },
      { path: 'teams/:id', element: <TeamDetail /> },

      { path: 'chat', element: <ChatList /> },
      { path: 'chat/:id', element: <ChatRoom /> },

      { path: 'profile', element: <Profile /> },
      { path: 'profile/:id', element: <UserProfile /> },
      // La v3 (fase 2, PR 7): UNA rotta per tutte le modalita' dei giochi, `/modalita/:chiave`, il gioco e' quello di `core.selectedGames`. Il confronto LETTERALE del flag sta in `lib/v3.ts`
      // (`V3_IN_BUILD`, `SchermataModalita`): in produzione la rotta NON esiste e il suo pezzo non c'e' nel bundle (`verify:v3-bundle`). Dove esiste ma la v3 non e' accesa
      // (build col flag, non beta o interruttore spento) la schermata rimanda a `/` con `replace`.
      ...(V3_IN_BUILD && SchermataModalita ? [{ path: 'modalita/:chiave', element: lazyRoute(<SchermataModalita />) }] : []),
      // La v3 (ADR-0264 §2): `/minigiochi`, la destinazione della quinta voce della barra, con l'elenco dei minigiochi. Esiste SOLO in AND: la v3 in build (`V3_IN_BUILD`, `lib/v3.ts`) E almeno un gioco con il
      // flag letterale acceso (`PaginaMinigiochi`, `lib/minigiochi.ts`); in produzione, coi flag spenti, NON c'e' ne' la rotta ne' il chunk (`verify-minigiochi --dist`). Dietro `RoleGate beta`; fuori dalla v3
      // la pagina rimanda a `/` (`MinigiochiPagina`). Non entra nello `start_param` ne' nei deep link (`tests/minigiochi-rotta.test.ts`).
      ...(V3_IN_BUILD && PaginaMinigiochi ? [{ path: 'minigiochi', element: <RoleGate role="beta">{lazyRoute(<PaginaMinigiochi />)}</RoleGate> }] : []),
      // DM fra amici: la conversazione, dietro l'opt-in di build (solo staging). L'ELENCO non ha ancora un
      // ingresso: aspetta L-2238 (switch in Amici · icona · Inbox).
      ...(env.featureDmEnabled ? [{ path: DM_PATH_CONVERSAZIONE, element: lazyRoute(<DmConversazione />) }] : []),

      { path: 'inbox', element: <Inbox /> },

      // League page gated by its own flag (split from the qbank wallet flag) so
      // PROD can keep the wallet beta on while the league stays held. When off,
      // `/league` falls through to the `*` catch-all → redirect to `/`.
      ...(env.featureLeagueEnabled ? [{ path: 'league', element: <League /> }] : []),

      { path: 'friends', element: <Friends /> },
      // «Account bloccati» (punto 7 del ridisegno Amici): sottopagina di Amici, quindi senza TabBar.
      { path: BLOCCATI_ROUTE, element: <FriendsBlocked /> },
      // «Notifiche» (impostazioni, L-2500): esiste solo dove l'API di consenso di QBot esiste (opt-in di build,
      // solo staging). Senza il flag `/friends/notifications` cade nel `*` e la riga in Amici non si monta.
      ...(env.featureNotificationSettingsEnabled
        ? [{ path: NOTIFICHE_ROUTE, element: <FriendsNotifiche /> }]
        : []),

      // Clash Royale account linking for 1v1 duels (qduels verify-token).
      // Prerequisite for the friends-list "Sfida" button — reached from
      // Profile → Linked accounts and from the gated Sfida affordance.
      { path: 'duels/link', element: <DuelLink /> },

      // Post-match duel result — outcome + both decks + opponent social/deck
      // actions. Reached from the QueueSheet (queue completion) and the Sfide
      // tab (tapping a completed challenge). Optional `?challengeId=` adds the
      // series X–Y header.
      { path: 'duels/result/:gameTag', element: lazyRoute(<DuelResult />) },

      // QDuels tournament detail — header, own-match state, current-round
      // bracket, roster, join/leave. Flag-gated like the lists that link here,
      // but note the reads themselves are NOT caller-scoped server-side: any
      // authenticated user can open any tournament by id, so a shared link
      // renders even for someone who never joined.
      // 🔴 NON REDIRIGERE QUESTA ROTTA VERSO LIMULO. La forma tentante e'
      // `<Navigate to={limuloTournamentUrl(tournamentId)} />`, e fabbricherebbe una
      // mappatura che non esiste: questo `:tournamentId` e' l'identificatore di un torneo
      // **qduels**, mentre le rotte Limulo vogliono il `tournamentId` di business di
      // **qTournaments**. Sono due spazi di identificatori senza nessuna corrispondenza.
      // ⚠️ Quanto e' sensibile quel parametro lo dice `lib/limulo/routes.ts`: perfino
      // passare l'`_id` di Mongo al posto del `tournamentId` GIUSTO fa rispondere 424 a tre
      // rotte su cinque (costato due PR, #122 e #123).
      // ⇒ Un link che porta a un torneo DIVERSO e' peggio di un link che non porta da
      // nessuna parte. La rotta resta spenta dal suo flag e sparisce col client qduels.
      // (Ragionamento esteso: `reports/2026-09-10-passo2-gruppo-a-disegno.md` §C.)
      ...(env.featureQduelsTournamentsEnabled
        ? [{ path: 'duels/tournaments/:tournamentId', element: lazyRoute(<DuelTournamentDetail />) }]
        : []),

      // Legacy leaderboard, gated like every other game surface. When off,
      // both fall through to the `*` catch-all → redirect to `/`.
      ...(env.featureLeaderboardEnabled
        ? [
            { path: 'leaderboards', element: <Leaderboards /> },
            { path: 'leaderboards/:id', element: <LeaderboardDetail /> },
          ]
        : []),

      { path: 'news', element: <News /> },
      { path: 'news/preferences', element: <NewsPreferences /> },
      { path: 'news/tag/:slug', element: <NewsTag /> },
      { path: 'news/:slug', element: <ArticleDetail /> },

      ...avatarRoutes,
      ...riotRoutes,
      ...crRoutes,
      ...chessRoutes,
      ...chessLimuloRoutes,
      ...chessBotRoutes,
      ...bridgeRoutes,
      ...burracoRoutes,
      ...scopaRoutes,
      ...briscolaRoutes,
      ...ginRummyRoutes,
      ...scala40Routes,
      ...settemezzoRoutes,
      ...tressetteRoutes,
      ...solitarioRoutes,
      ...q2048Routes,
      ...cocRoutes,
      // 🔑 `/invito/<codice>` sta QUI e non in `backgammonRoutes`: quelle
      // esistono solo col flag acceso e sono dentro <RoleGate role="beta">, e un
      // ospite senza ruolo cadrebbe sul catch-all `*` finendo sulla Hub — il
      // fallimento silenzioso che il deep link esiste per togliere. Questa
      // schermata c'e' sempre e dice quale dei casi e'. Vedi InviteLanding.tsx.
      { path: 'invito/:code', element: lazyRoute(<InviteLanding />) },
      // 🔑 Stesso motivo per `/inc/<tid>` (`inc_<tid>`, il DM «partita pronta», I-356): fuori da `limuloRoutes` (flag + beta), perche'
      // la schermata dice quale caso e' (flag spento, senza ruolo, errore di lettura) invece di far cadere il link sul catch-all.
      // Vedi IncontroLanding.tsx.
      { path: 'inc/:tournamentId', element: lazyRoute(<IncontroLanding />) },
      ...backgammonRoutes,
      ...missionsRoutes,
      ...chinesePokerRoutes,
      ...limuloRoutes,

      // La Pilotta (P8): un blocco a se', una costante (`pilottaRoutes`, sopra) e questo spread, prima del catch-all.
      ...pilottaRoutes,

      // Il Biriba (PR-A, lo scheletro): un blocco a se', una costante (`mpirimpaRoutes`, sopra) e questo spread, prima del catch-all.
      ...mpirimpaRoutes,

      { path: 'settings', element: <SettingsHome /> },
      // La schermata del nickname: prima del 2026-09-15 il nome si poteva scegliere
      // solo nel wizard di onboarding, che parte una volta sola e non e' riapribile.
      { path: 'settings/nickname', element: <NicknameSettings /> },
      { path: 'settings/preferences', element: <Preferences /> },
      { path: 'settings/appearance', element: <Appearance /> },
      { path: 'settings/accounts', element: <LinkedAccounts /> },
      // L'atterraggio di ritorno da Telegram per «Collega Telegram». Sta DENTRO
      // l'albero autenticato di proposito: chiude un giro aperto da una app gia'
      // avviata, e non ha niente a che vedere con `/auth/callback`, che e' il
      // ritorno del LOGIN e vive fuori dal Router (fast path di `main.tsx`).
      { path: 'auth/link-callback', element: <AuthLinkCallback /> },
      // Wallet & Livelli dietro lo STESSO flag del link in `SettingsList` (orc48, 2026-09-25).
      // Nascondere il solo link non bastava: per link diretto la pagina si montava e
      // `useGetWalletsQuery` partiva senza `skip` — su staging, col flag spento, una lettura
      // verso wallet.qlash.gg (la produzione) col token del tenant di staging. Forma del
      // precedente `league` qui sopra: rotta omessa → catch-all `*` → redirect a `/`.
      ...(env.featureQbankEnabled ? [{ path: 'settings/wallet', element: <WalletLevels /> }] : []),
      { path: 'settings/banner', element: <BannerSettings /> },
      { path: 'settings/communication', element: <Communication /> },
      // «Studio avatar» (I-416, L-2762): la porta della creazione, solo i ruoli creatore, flag opt-in acceso solo su
      // staging. Rotta omessa col flag spento → catch-all `*` → `/`, come `settings/wallet`. Richiede anche il flag
      // degli avatar: lo Studio rimanda a rotte che con quel flag spento non esistono.
      ...(env.featureAvatarsEnabled && env.featureAvatarStudioEnabled
        ? [
            {
              path: 'settings/studio-avatar',
              element: (
                <CreatoreGate>
                  <StudioAvatar />
                </CreatoreGate>
              ),
            },
          ]
        : []),

      ...sondaCatturaRoutes,

      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);
